// Pause, Confirm, Alert and Info (legacy window/pause.js, confirm.js, alert.js, info.js). The legacy Rules
// window is gone: the rules are a page of their own (rules/rulesUrl.ts, owner 2026-10-16).
import { useEffect, useState } from 'react';
import { APP_VERSION, env } from '../env';
import { hasKey, t } from '../i18n';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { WindowFrame } from './WindowFrame';
import { withIndicator } from '../ui/busy';
import { boardShot } from '../ui/boardShot';
import { newReportRef, sendPlayerReport } from '../ui/errorReport';
import { useController, useEngineState } from '../store/gameStore';
import { backSrc, faceSrc, faceSrcSet, tileDeckOf, tileSrc, tileSrcSet } from '../options/assets';
import { AdSlot } from './AdSlot';
import { useMultiplayerState } from '../multiplayer/store';
import { useFitWindow } from './useFitWindow';
import '../fonts/saloon.css'; // the saloon type of the titles and the numbers (Rye with Polish and other letters)
import type React from 'react';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** A HUD label's caption without its value ("Czas: {var}" -> "Czas"), for the Pause window's tickets. */
const caption = (key: string) => t(key, { var: '' }).replace(/[:：]/g, '').trim();

/** The treat drawn in the Pause window: a cup of coffee or tea (steam rising) or a cookie (crumbs falling). */
type Treat = 'coffee' | 'tea' | 'cookie';
const TREATS: Treat[] = ['coffee', 'tea', 'cookie'];

function TreatArt({ treat }: { treat: Treat }) {
  const steam = (
    <g className="pause_steam">
      <path d="M48 34c-6-7 6-11 0-19" />
      <path d="M60 32c-6-7 6-11 0-19" />
      <path d="M72 34c-6-7 6-11 0-19" />
    </g>
  );
  if (treat === 'cookie')
    return (
      <svg viewBox="0 0 120 120" className="pause_art pause_art_cookie">
        <ellipse cx="60" cy="104" rx="34" ry="6" fill="rgb(60 30 0 / 18%)" />
        <g className="pause_cookie">
          <path
            d="M60 22a40 40 0 1 0 39 31a10 10 0 0 1-13-9a10 10 0 0 1-11-12a10 10 0 0 1-15-10z"
            fill="#d99a4e"
            stroke="#a8692b"
            strokeWidth="3"
          />
          <path d="M60 30a32 32 0 1 0 32 30" fill="none" stroke="#e8b46f" strokeWidth="3" strokeLinecap="round" opacity=".7" />
          {[[44, 52, 5], [62, 66, 6], [42, 78, 4.5], [74, 86, 5], [56, 92, 4], [80, 64, 4], [50, 66, 3]].map(([x, y, r], i) => (
            <circle key={i} cx={x} cy={y} r={r} fill="#5a3418" />
          ))}
        </g>
        <g className="pause_crumbs" fill="#c98a43">
          <circle cx="92" cy="48" r="2.6" />
          <circle cx="98" cy="56" r="2" />
          <circle cx="88" cy="58" r="1.6" />
        </g>
      </svg>
    );
  const tea = treat === 'tea';
  return (
    <svg viewBox="0 0 120 120" className={`pause_art pause_art_${treat}`}>
      {steam}
      <ellipse cx="60" cy="102" rx="44" ry="9" fill="#e9dcc0" stroke="#b9a37a" strokeWidth="2.5" />
      <path d="M86 56c14-2 18 18 2 22" fill="none" stroke="#f7f1e3" strokeWidth="9" strokeLinecap="round" />
      <path d="M86 56c14-2 18 18 2 22" fill="none" stroke="#b9a37a" strokeWidth="2.5" strokeLinecap="round" opacity=".55" />
      <path d="M28 46h64l-6 40a14 14 0 0 1-14 12H48a14 14 0 0 1-14-12z" fill="#fbf6ea" stroke="#b9a37a" strokeWidth="2.5" />
      <ellipse cx="60" cy="46" rx="32" ry="7" fill={tea ? '#c47a1c' : '#5a3418'} stroke="#b9a37a" strokeWidth="2.5" />
      {tea ? (
        <g>
          <path d="M70 46c4-14 10-16 16-18" fill="none" stroke="#8a7a5c" strokeWidth="1.6" />
          <rect x="82" y="22" width="12" height="14" rx="2" fill="#d4e3c3" stroke="#6c9655" strokeWidth="2" transform="rotate(14 88 29)" />
        </g>
      ) : (
        <ellipse cx="54" cy="45" rx="12" ry="2.6" fill="#c89b6d" opacity=".75" />
      )}
    </svg>
  );
}

/** A random treat per pause. Not Math.random: the e2e tests seed it for the deals, and an extra call would
 *  change the next deal. Not the clock either: the screenshot tests got a different treat on each run. Under
 *  the e2e tests (`window.__E2E__`) it is always coffee, so the Pause screenshots are stable. */
function pickTreat(): Treat {
  if (window.__E2E__) return TREATS[0]!;
  return TREATS[crypto.getRandomValues(new Uint32Array(1))[0]! % TREATS.length]!;
}

/** The Pause window (owner 2026-10-18: "dostyluj ładnie okno pauzy", in the Win window's style, "kawę,
 *  herbatę, ciastko losowo pokazywane za każdą pauzą"): the Win window's cream paper with the card-back
 *  wallpaper and golden rays, an animated treat (see `pickTreat`), the title in Rye, the frozen time / score / moves on the same old tickets,
 *  the blue Resume button. The title is the first line of `pauseContent` (its "<br/>..." tail is dropped). */
export function PauseWindow({ instance, onResume }: { instance: WindowInstance; onResume: () => void }) {
  const s = useEngineState();
  const back = useUiState().options.skin.cardBackType;
  const [treat] = useState<Treat>(pickTreat);
  const title = t('pauseContent').split(/<br\s*\/?>/i)[0]!.trim();
  const stats: [string, string, string][] = [
    ['time', caption('labelTime'), `${pad2(Math.floor(s.seconds / 60))}:${pad2(s.seconds % 60)}`],
    ['score', caption('labelScore'), String(s.score)],
    ['moves', caption('labelMoves'), String(s.moves)],
  ];
  return (
    <WindowFrame name="pause" header={t('pauseHeader')} overlay closable={false} closing={instance.closing}>
      <div className="pause_backdrop" aria-hidden="true" style={{ '--pause-back': `url("${backSrc(back)}")` } as React.CSSProperties} />
      <div className="tab_contents">
        <div className="pause_cont" data-treat={treat}>
          <div className="pause_hero" aria-hidden="true">
            <div className="pause_rays" />
            <TreatArt treat={treat} />
          </div>
          <div className="pause_title td_content">{title}</div>
          <div className="pause_stats">
            {stats.map(([k, name, value]) => (
              <div key={k} className={`pause_stat pause_stat_${k}`}>
                <span>{name}</span>
                <b>{value}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="win_buttons">
          <input type="button" value={t('pauseButtonResume')} onClick={onResume} />
        </div>
      </div>
    </WindowFrame>
  );
}

/** Mahjong with no move left (owner 2026-10-24): a free shuffle of the tiles still on the board, an undo, or a new
 *  game (ui/mahjongHelp.ts opens it). The Pause window's look (windows.css "No moves"): the cream body with a faint
 *  wallpaper of the tile back, three tiles shuffling as the hero, the title in Rye, Shuffle the one big action.
 *  `cards` (owner 2026-10-26): a card game that can no longer be won (ui/lostGame.ts, an option): three cards of the
 *  player's deck as the hero, New game the big action, then Retry and Undo. Both carry the Win window's ad box
 *  (slot 2, owner 2026-10-26), between the text and the buttons. */
export function NoMovesWindow({ instance }: { instance: WindowInstance }) {
  const ctrl = useController();
  const skin = useUiState().options.skin;
  const cards = instance.props.cards === true;
  // an online race: a new deal would throw away the race's own (Shuffle and Undo stay)
  const racing = useMultiplayerState().gameStarted;
  const tiles = tileDeckOf(skin.deckType, skin.tileType);
  // Mahjong Connect: its own words (no line joins a pair), Mahjong's in a language without them yet
  const connect = ctrl.getState().game === 'mahjong-connect' && hasKey('mahjongConnect.noMoves.title');
  const close = () => uiStore().closeWindow('no_moves');
  // a low screen: the window drops its extras step by step until it fits (useFitWindow.ts, windows.css data-fit-*)
  useFitWindow('.lbx_window.lbx_no_moves', 4, cards);
  const act = (fn: () => void) => () => {
    close();
    fn();
  };
  return (
    <WindowFrame name="no_moves" header={t('mahjong.noMoves.header')} overlay closing={instance.closing} className={cards ? 'nomoves_cards' : ''}>
      <div className="pause_backdrop" aria-hidden="true" style={{ '--pause-back': `url("${cards ? backSrc(skin.cardBackType) : tileSrc(tiles, 'back')}")` } as React.CSSProperties} />
      <div className="tab_contents">
        <div className="nomoves_cont">
          <div className="nomoves_hero" aria-hidden="true">
            {cards
              ? ['Ah', 'Ks', 'Qd'].map((id, i) => (
                  <img key={id} className={`nomoves_tile nomoves_card nomoves_tile_${i}`} src={faceSrc(skin.deckType, id)} srcSet={faceSrcSet(skin.deckType, id)} alt="" draggable={false} />
                ))
              : ['f1', 'dr', 'p5'].map((id, i) => (
                  <img key={id} className={`nomoves_tile nomoves_tile_${i}`} src={tileSrc(tiles, id)} srcSet={tileSrcSet(tiles, id)} alt="" draggable={false} />
                ))}
          </div>
          <div className="nomoves_title">{t(cards ? 'lostGame.title' : connect ? 'mahjongConnect.noMoves.title' : 'mahjong.noMoves.title')}</div>
          <div className="nomoves_text">{t(cards ? 'lostGame.body' : connect ? 'mahjongConnect.noMoves.body' : 'mahjong.noMoves.body')}</div>
        </div>
        <AdSlot slot={2} />
        <div className="rules_buttons">
          {cards ? (
            <>
              <input type="button" className="nomoves_shuffle" value={t('menuNewGameButton')} onClick={act(() => ctrl.newGame())} />
              <input type="button" className="nomoves_other" value={t('menuRestartGameButton')} onClick={act(() => ctrl.retryGame())} />
              <input type="button" className="nomoves_other" value={t('menuUndo')} onClick={act(() => ctrl.undo())} />
            </>
          ) : (
            <>
              <input type="button" className="nomoves_shuffle" value={t('mahjong.shuffle')} onClick={act(() => ctrl.reshuffle())} />
              <input type="button" className="nomoves_other" value={t('menuUndo')} onClick={act(() => ctrl.undo())} />
              {racing ? null : <input type="button" className="nomoves_other" value={t('menuNewGameButton')} onClick={act(() => ctrl.newGame())} />}
            </>
          )}
        </div>
      </div>
    </WindowFrame>
  );
}

export function ConfirmWindow({ instance }: { instance: WindowInstance }) {
  const { header, body, onOk, onCancel } = instance.props as {
    header?: string;
    body?: string;
    onOk?: () => void;
    onCancel?: () => void;
  };
  const close = () => uiStore().closeWindow('confirm');
  return (
    <WindowFrame
      name="confirm"
      header={header ?? t('confirm.header')}
      overlay
      closing={instance.closing}
      onClose={onCancel}
    >
      <div className="tab_contents">
        <div className="rules_cont" dangerouslySetInnerHTML={{ __html: body ?? '' }} />
        <div className="rules_buttons">
          <input
            type="button"
            value={t('cancel')}
            onClick={() => {
              close();
              onCancel?.();
            }}
          />
          <input
            type="button"
            value="OK"
            onClick={() => {
              close();
              onOk?.();
            }}
          />
        </div>
      </div>
    </WindowFrame>
  );
}

export function AlertWindow({ instance }: { instance: WindowInstance }) {
  const { header, body } = instance.props as { header?: string; body?: string };
  return (
    <WindowFrame name="alert" header={header ?? t('alert.header')} closing={instance.closing}>
      <div className="tab_contents">
        <div className="rules_cont" dangerouslySetInnerHTML={{ __html: body ?? '' }} />
        <div className="rules_buttons">
          <input type="button" value="OK" onClick={() => uiStore().closeWindow('alert')} />
        </div>
      </div>
    </WindowFrame>
  );
}

/** The options as stored (the cookie's JSON, else the current values): the legacy mail carried them verbatim. */
function storedOptionsRaw(): string {
  const m = document.cookie.match(/(?:^|;\s*)opt=([^;]*)/);
  if (m) {
    try {
      return JSON.stringify(JSON.parse(decodeURIComponent(m[1]!)));
    } catch {
      /* fall through */
    }
  }
  const o = uiStore().getState().options;
  return JSON.stringify({ option: o.option, skin: o.skin });
}

/** What the owner needs to reproduce a report (owner 2026-10-23: "which way the phone is held"): orientation,
 *  the physical screen and pixel ratio, the visible area (the keyboard and browser bars shrink it), touch,
 *  the system's reduce-motion setting (the game ignores it, players do not know they have it), installed app
 *  or browser tab, and the build. */
function deviceDetails(): string {
  const portrait = matchMedia('(orientation: portrait)').matches;
  const type = (screen.orientation as ScreenOrientation | undefined)?.type ?? (portrait ? 'portrait' : 'landscape');
  const vv = window.visualViewport;
  const parts = [
    `orientacja ${portrait ? 'pionowa' : 'pozioma'} (${type})`,
    `ekran ${screen.width}x${screen.height} @${Math.round(devicePixelRatio * 100) / 100}x`,
    vv ? `widoczne ${Math.round(vv.width)}x${Math.round(vv.height)}, zoom ${Math.round(vv.scale * 100) / 100}` : '',
    `dotyk ${navigator.maxTouchPoints > 0 ? `tak (${navigator.maxTouchPoints})` : 'nie'}`,
    `animacje systemowe ${matchMedia('(prefers-reduced-motion: reduce)').matches ? 'wyłączone' : 'włączone'}`,
    matchMedia('(display-mode: standalone)').matches ? 'aplikacja (PWA)' : 'karta przeglądarki',
    `wersja ${env.revision || '?'}`,
  ];
  return parts.filter(Boolean).join(', ');
}

/** The server's rule (routes/contact.js tooShort): from "Treść:" to the end of the text, at least 40 bytes. */
export function tooShortMessage(message: string): boolean {
  return new TextEncoder().encode(`Treść: ${message}\n\n`).length < 40;
}

/** Contact form. Sends GET contact.html?<text> to the page's own origin (legacy: a hidden iframe to the production host). */
export function InfoWindow({ instance }: { instance: WindowInstance }) {
  const [sent, setSent] = useState(false);
  const [short, setShort] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  // A picture of the board and the game's state go with the message (owner 2026-10-25, ui/errorReport.ts): on
  // unless the player unticks it; the picture is taken when the window opens, so it shows the board as it was.
  const [attach, setAttach] = useState(true);
  const [shot, setShot] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void boardShot().then((s) => live && setShot(s));
    return () => {
      live = false;
    };
  }, []);
  const send = async () => {
    if (!message.trim()) return;
    // The server drops a message whose "Treść:" part is under 40 bytes (routes/contact.js, the PHP rule) and
    // answers "too short message"; the window said "sent" anyway (owner 2026-10-23: a short test never reached
    // /admin). Checked here first, and the answer too.
    if (tooShortMessage(message)) {
      setShort(true);
      return;
    }
    // The legacy message layout (contact.html parses nothing; the owner reads the mail). The report's id ties the
    // message to its picture and data in /admin (server/routes/admin.js).
    const ref = attach ? newReportRef() : null;
    const text = `Źródło: (${APP_VERSION})\n\n${navigator.userAgent}\n\nScreen roboczy: ${innerWidth}x${innerHeight}\n\nUrządzenie: ${deviceDetails()}\n\nUstawienia: ${storedOptionsRaw()}\n\n${ref ? `Zgłoszenie techniczne: #${ref}\n\n` : ''}${email.trim() ? `Email: ${email.trim()}\n\n` : ''}Treść: ${message}\n\n`;
    let ok: boolean | 'short' = false;
    try {
      ok = await withIndicator(async () => {
        const res = await fetch(`${env.fullAddr}contact.html?${encodeURIComponent(text)}`, { credentials: 'include' });
        const answer = res.ok ? (await res.text()).trim() : '';
        if (answer === 'too short message') return 'short';
        if (answer === 'ok' && ref) await sendPlayerReport(ref, message, shot);
        return answer === 'ok';
      });
    } catch {
      ok = false;
    }
    if (ok === 'short') {
      setShort(true);
      return;
    }
    setSent(true);
  };
  return (
    <WindowFrame name="info" header={t('infoHeader')} closing={instance.closing}>
      <div className="info_content">
        <div className="tab_buttons">
          <a href="#" className="tab_selected" onClick={(e) => e.preventDefault()}>
            {t('infoTabContactHeader')}
          </a>
        </div>
        <div className="tab_contents">
          <div className="contact_cont">
            {sent ? (
              <div id="contact_sent">
                <p className="contact_sent">{t('infoTabContactSent')}</p>
                <input
                  type="button"
                  id="contact_back"
                  value={t('infoTabContactBack')}
                  onClick={() => {
                    setSent(false);
                    setMessage('');
                  }}
                />
              </div>
            ) : (
              <form
                id="contact_form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
              >
                <p className="contact_label">{t('infoTabContactLabel')}</p>
                <div className="contact_form">
                  <textarea
                    id="contact_message"
                    autoFocus
                    placeholder={t('infoTabContactMessagePlaceholder')}
                    value={message}
                    aria-describedby={short ? 'contact_short' : undefined}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      setShort(false);
                    }}
                  />
                  {short ? (
                    <p id="contact_short" className="contact_short" role="alert">
                      {t('infoTabContactTooShort')}
                    </p>
                  ) : null}
                  <label className="contact_attach">
                    <input type="checkbox" id="contact_attach" checked={attach} onChange={(e) => setAttach(e.target.checked)} />
                    <span>{t('contact.attach')}</span>
                    {shot ? <img className={attach ? undefined : 'off'} src={shot} alt="" /> : null}
                  </label>
                  <input
                    type="text"
                    id="contact_email"
                    placeholder={t('infoTabContactEmailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <input
                    type="button"
                    id="contact_send"
                    value={t('infoTabContactSubmit')}
                    onClick={() => void send()}
                  />
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </WindowFrame>
  );
}
